import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BillingApiService } from 'src/app/core/services/billing-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';

// Mirrors TDS_SECTIONS in the backend; kept in sync manually because the rates are regulatory.
const TDS_SECTIONS = [
  { value: 'INTEREST_10', label: 'Interest (10%)', percent: 10 },
  { value: 'CONTRACTOR_1', label: 'Contractor (1%)', percent: 1 },
  { value: 'COMMISSION_5', label: 'Commission (5%)', percent: 5 },
  { value: 'PROFESSIONAL_10', label: 'Professional (10%)', percent: 10 },
];

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Indian bank account numbers are numeric and typically 9–18 digits. */
const ACCOUNT_NUMBER_RE = /^\d{9,18}$/;

@Component({
  selector: 'app-record-payment',
  standalone: true,
  imports: [PageSurfaceHeaderComponent, CommonModule, FormsModule],
  templateUrl: './record-payment.component.html',
})
export class RecordPaymentComponent implements OnInit {
  private readonly api = inject(BillingApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly tdsSections = TDS_SECTIONS;

  invoiceId = 0;
  /** The invoice's client, so Cancel and a saved payment return to that client's list. */
  tenantId: number | null = null;
  businessName = '';
  invoiceNumber = '';
  basicAmount = 0;
  cgst = 0;
  sgst = 0;
  totalInvoice = 0;
  receivedAmount = 0;

  /** True once any payment on this invoice carries TDS — TDS may be recorded only once. */
  tdsAlreadyRecorded = false;

  /** True when payment history failed to load — TDS status is unknown, so TDS must be blocked. */
  tdsHistoryLoadError = false;

  /** True when the invoice itself failed to load — every guard below depends on its figures, so submit must be blocked. */
  invoiceLoadError = false;

  reference = '';
  accountHolderName = '';
  accountNumber = '';
  accountNumberTouched = false;
  paidAmount: number | null = null;
  paidAmountTouched = false;
  /** True while paidAmount is still the auto-filled balance, so TDS changes may re-derive it. */
  private amountDefaulted = false;
  paymentDate = new Date().toISOString().slice(0, 10);
  taxDeducted = false;
  tdsSection = '';

  submitting = false;
  submitError = '';

  get remainingAmount(): number {
    return round2(this.totalInvoice - this.receivedAmount);
  }

  /** TDS is a percentage of the invoice's basic amount, independent of the amount paid. */
  get tdsAmount(): number {
    if (!this.taxDeducted || !this.tdsSection) return 0;
    const section = TDS_SECTIONS.find((s) => s.value === this.tdsSection);
    return section ? round2((this.basicAmount * section.percent) / 100) : 0;
  }

  get totalPaymentRecorded(): number {
    return round2((this.paidAmount ?? 0) + this.tdsAmount);
  }

  get isAccountNumberValid(): boolean {
    return ACCOUNT_NUMBER_RE.test(this.accountNumber.trim());
  }

  get accountNumberError(): string | null {
    if (!this.accountNumberTouched) return null;
    const raw = this.accountNumber.trim();
    if (!raw) return 'Account number is required.';
    if (!ACCOUNT_NUMBER_RE.test(raw)) {
      return 'Enter a valid account number (9–18 digits).';
    }
    return null;
  }

  get exceedsRemainingAmount(): boolean {
    return (this.paidAmount ?? 0) > 0 && this.totalPaymentRecorded > this.remainingAmount + 0.005;
  }

  get paidAmountError(): string | null {
    const paid = this.paidAmount ?? 0;
    if (paid <= 0) {
      return this.paidAmountTouched ? 'Paid amount must be greater than zero.' : null;
    }
    if (this.exceedsRemainingAmount) {
      const remaining = this.remainingAmount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (this.tdsAmount > 0) {
        return `Total payment recorded (paid + TDS) cannot exceed the remaining amount of ${remaining}.`;
      }
      return `Paid amount cannot exceed the remaining amount of ${remaining}.`;
    }
    return null;
  }

  get canSubmit(): boolean {
    return (
      !this.submitting &&
      !this.invoiceLoadError &&
      !!this.reference.trim() &&
      !!this.accountHolderName.trim() &&
      this.isAccountNumberValid &&
      (this.paidAmount ?? 0) > 0 &&
      (!this.taxDeducted || !!this.tdsSection) &&
      this.totalPaymentRecorded <= this.remainingAmount + 0.005
    );
  }

  ngOnInit(): void {
    this.invoiceId = Number(this.route.snapshot.paramMap.get('invoiceId'));

    this.api.getInvoice(this.invoiceId).subscribe({
      next: (res) => {
        const inv = res.data;
        this.invoiceNumber = inv.invoice_number;
        this.businessName = inv.tenant?.tenant_name ?? '';
        this.basicAmount = inv.subtotal;
        // GST is split evenly into CGST and SGST for display.
        this.cgst = round2(inv.gst_amount / 2);
        this.sgst = round2(inv.gst_amount / 2);
        this.totalInvoice = inv.total;
        this.receivedAmount = inv.paid_amount ?? 0;
        this.tenantId = inv.tenant_id ?? null;
        // Most payments settle what is left; the operator edits it for a part payment.
        if (this.paidAmount === null && this.remainingAmount > 0) {
          this.amountDefaulted = true;
          this.applyDefaultAmount();
        }
      },
      // Every figure on screen (and the remaining-balance submit guard) depends on the invoice, so a
      // failed fetch must block submit rather than leave a blank, zero-amount screen that looks safe.
      error: () => {
        this.invoiceLoadError = true;
      },
    });

    this.api.getPaymentHistory(this.invoiceId).subscribe({
      next: (res) => {
        this.tdsAlreadyRecorded = res.data.grand_total_tds > 0;
      },
      // Fail closed: if we can't confirm whether TDS was already recorded, don't leave the TDS
      // radios enabled — the server allows TDS only once per invoice and would reject it anyway.
      error: () => {
        this.tdsHistoryLoadError = true;
      },
    });
  }

  onTaxDeductedChange(value: boolean): void {
    this.taxDeducted = value;
    if (!value) this.tdsSection = '';
    this.submitError = '';
    this.paidAmountTouched = true;
    this.applyDefaultAmount();
  }

  onTdsSectionChange(): void {
    this.paidAmountTouched = true;
    this.applyDefaultAmount();
  }

  // TDS is part of the payment recorded, so an untouched default must leave room for it.
  private applyDefaultAmount(): void {
    if (!this.amountDefaulted) return;
    this.paidAmount = round2(Math.max(this.remainingAmount - this.tdsAmount, 0));
  }

  onPaidAmountChange(value: number | null): void {
    this.amountDefaulted = false;
    this.paidAmount = value;
    this.paidAmountTouched = true;
    this.submitError = '';
  }

  onPaidAmountBlur(): void {
    this.paidAmountTouched = true;
  }

  onAccountNumberChange(value: string): void {
    this.accountNumber = value.replaceAll(/\D/g, '').slice(0, 18);
    this.accountNumberTouched = true;
    this.submitError = '';
  }

  onAccountNumberBlur(): void {
    this.accountNumberTouched = true;
  }

  submit(): void {
    this.accountNumberTouched = true;
    this.paidAmountTouched = true;
    if (!this.canSubmit) return;
    this.submitting = true;
    this.submitError = '';

    this.api
      .recordPayment(this.invoiceId, {
        reference: this.reference.trim(),
        account_holder_name: this.accountHolderName.trim(),
        account_number: this.accountNumber.trim(),
        paid_amount: this.paidAmount ?? 0,
        payment_date: new Date(this.paymentDate).toISOString(),
        ...(this.taxDeducted && this.tdsSection ? { tds_section: this.tdsSection } : {}),
      })
      .subscribe({
        next: () => this.cancel(),
        error: (err) => {
          this.submitError = err?.error?.message ?? 'Could not record the payment.';
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    void this.router.navigate(['/invoices'], { queryParams: this.tenantId ? { client: this.tenantId } : {} });
  }
}
